import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { AdminShell } from '../../components/AdminShell';
import { formatDate } from '../../lib/format';

interface EmailLogRow {
  id: number;
  template: string;
  recipient: string;
  locale: string | null;
  status: 'queued' | 'sent' | 'failed';
  reason: string | null;
  created_at: string;
}

const TONE: Record<EmailLogRow['status'], 'muted' | 'success' | 'warn'> = {
  queued: 'muted',
  sent: 'success',
  failed: 'warn',
};

/** Admin → Email log. Read-only delivery diagnostics for transactional email. */
export function AdminEmailLogPage() {
  const { t, i18n } = useTranslation();
  const rows = useQuery<EmailLogRow[]>({
    queryKey: ['admin-email-log'],
    queryFn: async () => (await api.get('/admin/email-log?limit=200')).data,
    refetchInterval: 30_000,
  });

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
        <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.emailLog.title')}</h1>
        <p className="text-sm text-ink-600 dark:text-ink-300 mt-1">{t('admin.emailLog.subtitle')}</p>

        <Card className="mt-6 overflow-hidden">
          {rows.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !rows.data || rows.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('admin.emailLog.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 dark:bg-ink-800/50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.template')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.recipient')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.locale')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.status')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.reason')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.emailLog.col.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.map((r) => (
                    <tr key={r.id} className="border-t border-ink-200 dark:border-ink-800">
                      <td className="px-4 py-3"><span className="font-mono text-xs">{r.template}</span></td>
                      <td className="px-4 py-3">{r.recipient}</td>
                      <td className="px-4 py-3 text-xs text-ink-500">{r.locale ?? '—'}</td>
                      <td className="px-4 py-3"><Pill tone={TONE[r.status]}>{t(`admin.emailLog.statuses.${r.status}`)}</Pill></td>
                      <td className="px-4 py-3 text-xs text-warn-600 max-w-[280px] truncate" title={r.reason ?? undefined}>{r.reason ?? '—'}</td>
                      <td className="px-4 py-3 num text-ink-500 text-xs">{formatDate(r.created_at, i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AdminShell>
  );
}
